import { useState, useEffect, useCallback } from "react";
import {
  loginOperatorInCrane,
  logoffOperatorOfCrane,
  getCraneOfOperator,
} from "../../repository/BilletYardInventoryRepository";
import { useProfiles } from "./useProfiles";
import { useOperatorSession } from "./useOperatorSession";

/**
 * Hook to manage crane-operator session based on new profile system
 * Maps new system users to old system operator IDs for proxy compatibility
 */
export const useCraneSession = () => {
  const { isOperator, isSupervisor } = useProfiles();
  const { selectedOperatorId } = useOperatorSession();
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [currentCraneId, setCurrentCraneId] = useState<number | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [isRecoveringSession, setIsRecoveringSession] = useState(false);

  // Get current operator ID from operator session - FIXED
  const getCurrentOperatorId = useCallback((): number | null => {
    // Only BYI operators and supervisors can access cranes
    if (!isOperator && !isSupervisor) {
      return null; // No crane access for non-BYI users
    }

    // Return selected operator ID from operator session
    // If no operator selected, return fallback to 18 to prevent crashes
    return selectedOperatorId || 18;
  }, [isOperator, isSupervisor, selectedOperatorId]);

  // Recover existing session on app startup/refresh
  const recoverExistingSession = useCallback(
    async (isMounted?: () => boolean): Promise<void> => {
      const operatorId = getCurrentOperatorId();

      if (!operatorId) {
        return; // No operator mapping, can't recover session
      }

      if (isMounted && !isMounted()) return;
      setIsRecoveringSession(true);
      setSessionError(null);

      try {
        const response = await getCraneOfOperator(operatorId);

        // Check if component is still mounted before setting state
        if (isMounted && !isMounted()) return;

        // Check if operator has an active crane session
        const craneId = response?.ok
          ? response.data?.tables?.[0]?.rows?.[0]?.CraneID
          : null;

        if (craneId && craneId !== 0) {
          setCurrentCraneId(craneId);
          setIsSessionActive(true);
        } else {
          setCurrentCraneId(null);
          setIsSessionActive(false);
        }
      } catch (error) {
        // Don't set error state - just continue without recovery
        if (isMounted && !isMounted()) return;
        setCurrentCraneId(null);
        setIsSessionActive(false);
      } finally {
        if (isMounted && !isMounted()) return;
        setIsRecoveringSession(false);
      }
    },
    [getCurrentOperatorId],
  );

  // Run session recovery on mount and when operator changes
  useEffect(() => {
    let isMounted = true;

    const runRecovery = async () => {
      if (isMounted) {
        await recoverExistingSession(() => isMounted);
      }
    };

    runRecovery();

    return () => {
      isMounted = false;
    };
  }, [recoverExistingSession]);

  // Login operator to crane (notifies database)
  const loginToCrane = useCallback(
    async (craneId: number): Promise<boolean> => {
      // Skip login API calls for BYI Supervisors - they don't need to login to cranes
      if (isSupervisor) {
        setIsSessionActive(true);
        setCurrentCraneId(craneId);
        return true;
      }

      const operatorId = getCurrentOperatorId();

      if (!operatorId) {
        setSessionError("No operator mapping found for current user profile");
        return false;
      }

      if (craneId === 0) {
        setSessionError('Cannot login to "Unknown" crane');
        return false;
      }

      try {
        setSessionError(null);

        // First attempt: try to login directly
        let response = await loginOperatorInCrane(
          craneId,
          operatorId,
          true, // checkValidation
          true, // checkOperatorLogged
          true, // checkProcParams
          true, // checkExistence
        );

        // If login failed due to another operator being logged in, try to clear the crane first
        const responseMessage =
          response?.ok && "data" in response
            ? response.data?.tables?.[0]?.rows?.[0]?.Message || ""
            : "";
        const firstResponseId =
          response?.ok && "data" in response
            ? response.data?.tables?.[0]?.rows?.[0]?.ResponseID
            : undefined;
        if (
          response?.ok &&
          firstResponseId !== 0 &&
          responseMessage.toLowerCase().includes("another operator")
        ) {
          // Attempt to logoff any existing operator from this crane
          await logoffOperatorOfCrane(
            craneId,
            true, // checkProcParams
            true, // checkExistence
          );

          // Try login again after clearing the crane
          response = await loginOperatorInCrane(
            craneId,
            operatorId,
            true, // checkValidation
            true, // checkOperatorLogged
            true, // checkProcParams
            true, // checkExistence
          );
        }

        // Check if login was successful based on response structure
        const responseId =
          response?.ok && "data" in response
            ? response.data?.tables?.[0]?.rows?.[0]?.ResponseID
            : undefined;
        const message =
          response?.ok && "data" in response
            ? response.data?.tables?.[0]?.rows?.[0]?.Message
            : response?.message;

        if (response?.ok && responseId === 0) {
          setIsSessionActive(true);
          setCurrentCraneId(craneId);
          return true;
        } else {
          const errorMessage = message || "Login failed";
          setSessionError(errorMessage);
          return false;
        }
      } catch (error) {
        setSessionError(
          "Failed to login to crane: " + (error as Error).message,
        );
        return false;
      }
    },
    [getCurrentOperatorId],
  );

  // Logoff operator from crane (notifies database)
  const logoffFromCrane = useCallback(async (): Promise<boolean> => {
    if (!currentCraneId) {
      return true; // Already logged off
    }

    // Skip logoff API calls for BYI Supervisors - they don't need to logoff from cranes
    if (isSupervisor) {
      setIsSessionActive(false);
      setCurrentCraneId(null);
      return true;
    }

    try {
      setSessionError(null);

      const response = await logoffOperatorOfCrane(
        currentCraneId,
        true, // checkProcParams
        true, // checkExistence
      );

      // Check if logoff was successful
      const logoffResponseId =
        response?.ok && "data" in response
          ? response.data?.tables?.[0]?.rows?.[0]?.ResponseID
          : undefined;
      const logoffMessage =
        response?.ok && "data" in response
          ? response.data?.tables?.[0]?.rows?.[0]?.Message
          : response?.message;

      if (response?.ok && logoffResponseId === 0) {
        setIsSessionActive(false);
        setCurrentCraneId(null);
        return true;
      } else {
        const errorMessage = logoffMessage || "Logoff failed";
        setSessionError(errorMessage);
        return false;
      }
    } catch (error) {
      setSessionError(
        "Failed to logoff from crane: " + (error as Error).message,
      );
      return false;
    }
  }, [currentCraneId]);

  // Auto-logoff when component unmounts or user role changes
  useEffect(() => {
    let isMounted = true;
    return () => {
      isMounted = false;
      if (isSessionActive && currentCraneId && !isSupervisor) {
        // Only call logoff API for non-supervisors
        // Don't await this to prevent blocking component unmount
        // Also don't call logoffFromCrane since it sets state and causes memory leak
        logoffOperatorOfCrane(currentCraneId, true, true).catch(() => {});
      }
    };
  }, [isSessionActive, currentCraneId, isSupervisor]);

  return {
    isSessionActive,
    currentCraneId,
    sessionError,
    isRecoveringSession,
    loginToCrane,
    logoffFromCrane,
    recoverExistingSession,
    canManageCraneSessions: isOperator || isSupervisor,
    // Current operator ID for debugging/monitoring
    currentOperatorId: getCurrentOperatorId(),
  };
};
